import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface MarketingAutomaticoItem {
    id: number
    evento: string
    tempoPosEvento: number
    tempoFinal: number | null
    diaParaEnviar: number
    horaDisparo: string
    tituloMensagem: string
    descricaoMensagem: string
    criarCupom: boolean
    nomeCupom: string | null
    diasValidade: number | null
    tipoDesconto: boolean
    valorDesconto: number | null
    valorMinimo: number | null
    quantCuponsEnviados: number
    quantUtilizadaCupom: number
}

export interface SalvarMarketingPayload {
    id: number | null
    evento: string
    tempoPosEvento: number
    tempoFinal: number | null
    diaParaEnviar: number
    hora: string
    tituloMensagem: string
    descricaoMensagem: string
    criarCupom: boolean
    nomeCupom?: string
    diasValidade?: number
    tipoDesconto?: boolean
    valorDesconto?: number
    valorMinimo?: number
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }
type RespostaErroRaw = { codErro: number; mensagem?: string }

function isErroBackend(data: unknown): data is RespostaErroRaw {
    return (
        typeof data === "object" &&
        data !== null &&
        !Array.isArray(data) &&
        "codErro" in data &&
        (data as RespostaErroRaw).codErro > 0
    )
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

export const obterMarketingAutomatico = async function (
    authorization: string
): Promise<MarketingAutomaticoItem[] | ErroApi> {
    try {
        const response = await api
            .get<MarketingAutomaticoItem[] | RespostaErroRaw>(
                "estrategias/ObterMarketingAutomatico",
                {
                    headers: { "Content-Type": "application/json", Authorization: authorization },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        if (response.data === null || response.data === undefined) return []
        return response.data as MarketingAutomaticoItem[]
    } catch (error) {
        RegistrarLogErro(error, "ObterMarketingAutomatico", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarMarketingAutomatico = async function (
    authorization: string,
    payload: SalvarMarketingPayload
): Promise<MarketingAutomaticoItem | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<MarketingAutomaticoItem>>(
                "estrategias/SalvarMarketingAutomatico",
                payload,
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        const { data } = response
        if (data.codErro !== undefined && data.codErro > 0) {
            return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
        }
        return data
    } catch (error) {
        RegistrarLogErro(error, "SalvarMarketingAutomatico", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const excluirMarketingAutomatico = async function (
    authorization: string,
    id: number
): Promise<void | ErroApi> {
    try {
        const response = await api
            .delete<RespostaErroRaw>(`estrategias/ExcluirMarketingAutomatico/${id}`, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
    } catch (error) {
        RegistrarLogErro(error, "ExcluirMarketingAutomatico", String(id))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const executarMarketingAutomatico = async function (
    authorization: string,
    id: number
): Promise<{ qtdImpactados: number } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ qtdImpactados: number }>>(
                `estrategias/ExecutarMarketingAutomatico/${id}`,
                {},
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        const { data } = response
        if (data.codErro !== undefined && data.codErro > 0) {
            return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
        }
        return data as { qtdImpactados: number }
    } catch (error) {
        RegistrarLogErro(error, "ExecutarMarketingAutomatico", String(id))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const criarEstrategiasPadrao = async function (
    authorization: string
): Promise<void | ErroApi> {
    try {
        const response = await api
            .post<RespostaErroRaw>(
                "estrategias/CriarEstrategiasPadrao",
                {},
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
    } catch (error) {
        RegistrarLogErro(error, "CriarEstrategiasPadrao", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
